// Builds a draft invoice config for an engagement from what the Hub already
// knows, so the form opens nearly complete. Nothing here writes anywhere.
//
// Config shape (the thread-invoice skill's schema, so the same JSON drives
// docs/invoice/generate_invoice.py):
// { invoice_number, issue_date: "YYYY-MM-DD", service_period: "Nov '26",
//   client_short, bill_to: { name, address, email }, currency: "USD",
//   line_items: [{ description, amount }], gst: "exempt",
//   from?: { entity, address, contact, website }, payment_details?: [[label, value]] }
//
// Optional per-engagement defaults live in Finance Config (raw, top level):
//   "invoicing": { "billTo": { name, address, email }, "clientShort": "Keynes" }
// They are inert for rendering.
import normalizeConfig from "../finance/normalizeConfig";
import { invoicingOf } from "../finance/presets";
import { addMonths, rosterAdditionsForMonth } from "../finance/calc";
import { orgName, contactName, contactEmail } from "../engagement";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function servicePeriodLabel(ym) {
  const [y, m] = ym.split("-");
  return `${MONTHS[Number(m) - 1]} '${y.slice(2)}`;
}

// Every invoice number the engagement has recorded, newest series wins:
// INV-KD-371 → INV-KD-372. "" when nothing is recorded (the user types it).
export function invoiceNumbersFor(cfg, data) {
  const m = cfg.modules || {};
  const out = [];
  (m.retainer?.invoices || []).forEach(i => i.number && out.push(i.number));
  (m.sessionBlock?.invoices || []).forEach(i => i.number && out.push(i.number));
  Object.values(data?.months || {}).forEach(md => (md.lineItems || []).forEach(li => li.invoice && out.push(li.invoice)));
  (data?.extras || []).forEach(x => x.invoice && out.push(x.invoice));
  return out;
}
// The highest-numbered invoice recorded, shown as a hint beside the number
// field. The number itself is typed in from Xero, which owns the sequence.
export function lastInvoiceNumber(numbers) {
  let best = null;
  numbers.forEach(n => {
    const m = /^(.*?)(\d+)$/.exec(String(n).trim());
    if (m && (!best || Number(m[2]) > best.n)) best = { n: Number(m[2]), raw: String(n).trim() };
  });
  return best ? best.raw : "";
}
export function nextInvoiceNumber(numbers) {
  let best = null;
  numbers.forEach(n => {
    const m = /^(.*?)(\d+)$/.exec(String(n).trim());
    if (m && (!best || Number(m[2]) > best.n)) best = { prefix: m[1], n: Number(m[2]), width: m[2].length };
  });
  return best ? `${best.prefix}${String(best.n + 1).padStart(best.width, "0")}` : "";
}

// House style for invoice line text (28 Oct 2026, modelled on INV-KD-371):
// a lowercase x for quantity, a spaced hyphen for a qualifier, commas for
// lists, no typographic symbols. Applied when lines are drafted and when a
// generated invoice is recorded; stored labels are never rewritten in place.
export function tidyLine(s) {
  return String(s ?? "")
    .replace(/\s*[×✕✖]\s*/g, " x ")
    .replace(/\s*[—–]\s*/g, " - ")
    .replace(/\s*[|·•]\s*/g, " - ")
    .replace(/\s+-\s+-\s+/g, " - ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

const seatLabel = (cfg, type) => (cfg.modules.recurringSessions?.seats || []).find(s => s.type === type)?.label
  || (type === "leadership" ? "Leadership coaching" : type === "team" ? "Team coaching" : "Coaching");

// Lines the service month should carry: the retainer baseline as one line
// (labelled like the last invoice), then each roster addition as its own
// line, so headcount changes stay traceable on the invoice.
export function draftLineItems(cfg, month, data) {
  const lines = [];
  const r = cfg.modules.retainer;
  if (r) {
    lines.push({ description: "Performance & Wellbeing Consultancy (approved baseline)", amount: r.monthlyTotal ?? r.amount ?? "", kind: "base" });
  }
  // Additions sit above the core every month; "new joiner" only in the month they join.
  rosterAdditionsForMonth(cfg, month).forEach(c => {
    // A roster entry with `detail` (e.g. "extra session") is an existing person
    // getting more, not a joiner; the detail is the qualifier instead.
    const qualifier = c.detail ? ` - ${c.detail}` : c.isNew ? " - new joiner" : "";
    lines.push({ description: tidyLine(`${seatLabel(cfg, c.type)} x 1 (${c.label})${qualifier}`), amount: `=${c.monthly}*1`, kind: "addition" });
  });
  // Work marked "invoice separately" in the Hub and still to invoice goes on
  // the invoice automatically (Alex, 29 Oct 2026); it carries its Hub key so
  // Record in Hub can mark it invoiced.
  openWork(data, month).forEach(w => lines.push({ description: w.description, amount: w.amount, ref: w.key, kind: "oneoff" }));
  return lines;
}

// Billed-separately line items (dated up to and including the service month)
// and extras, still at "to-invoice".
export function openWork(data, month) {
  const open = [];
  Object.entries(data?.months || {}).sort().forEach(([ym, md]) => {
    if (ym > month) return;
    (md.lineItems || []).forEach(li => {
      if (li.billedSeparately && (li.status || "to-invoice") === "to-invoice") open.push({ key: `li-${ym}-${li.id}`, description: tidyLine(li.label), amount: li.amount });
    });
  });
  (data?.extras || []).forEach(x => {
    if ((x.status || "to-invoice") === "to-invoice") open.push({ key: `ex-${x.id}`, description: tidyLine(x.label), amount: x.amount });
  });
  return open;
}

// One-click additions: work already marked "to invoice" in the Hub, then the
// engagement's own rate card (per-unit rates only; monthly retainer
// components are already inside the baseline).
export function quickAdds(cfg, data, month) {
  const open = openWork(data, month);
  const rates = (cfg.modules.rateCard?.rates || [])
    .filter(r => r.rate && (r.unit || "").trim() !== "/mo")
    .map(r => ({ key: `rate-${r.id}`, description: `${r.label} x 1`, amount: `=${r.rate}*1`, hint: `$${r.rate}${r.unit || ""}` }));
  return { open, rates };
}

function parse(json) {
  if (!json) return null;
  try { return JSON.parse(json); } catch (e) { return null; }
}

// fields = an Engagements record's fields. today = Date. remembered = the
// last bill-to / from used for this engagement in this browser, if any.
export function draftInvoice(fields, today = new Date(), remembered = {}) {
  const raw = parse(fields["Finance Config"]) || {};
  const data = parse(fields["Finance Data"]) || {};
  const cfg = normalizeConfig(raw);
  const thisMonth = isoDate(today).slice(0, 7);
  const month = addMonths(thisMonth, 1); // invoiced in advance of the service period
  const inv = raw.invoicing || {};
  const numbers = invoiceNumbersFor(cfg, data);
  return {
    month,
    lastNumber: lastInvoiceNumber(numbers),
    config: {
      invoice_number: nextInvoiceNumber(numbers),
      issue_date: isoDate(today),
      service_period: servicePeriodLabel(month),
      client_short: inv.clientShort || orgName(fields) || "",
      // The stored bill-to (Setup → Invoicing) always wins; what the browser remembers is only a fallback.
      bill_to: inv.billTo ? { name: "", address: "", email: "", ...inv.billTo } : remembered.bill_to || { name: contactName(fields) || "", address: "", email: contactEmail(fields) || "" },
      currency: cfg.currency || "USD",
      line_items: draftLineItems(cfg, month, data),
      gst: cfg.tax === "none" ? "exempt" : "",
      ...(remembered.from ? { from: remembered.from } : {}),
    },
    quick: quickAdds(cfg, data, month),
    cfg,
  };
}

// Draft for a different service month (the form's month picker).
export function linesForMonth(fields, month) {
  const cfg = normalizeConfig(parse(fields["Finance Config"]) || {});
  return draftLineItems(cfg, month, parse(fields["Finance Data"]) || {});
}

// A recorded invoice's lines may predate `kind`: infer base / addition / one-off.
export function inferKind(line, index) {
  if (line.kind) return line.kind;
  if (index === 0 && /consultancy|retainer|baseline/i.test(line.label || line.description || "")) return "base";
  if (/coaching x 1 \(/i.test(line.label || line.description || "")) return "addition";
  return "oneoff";
}

export function invoiceFilename(config, ext = "xlsx") {
  return `Thread - ${config.client_short || "Client"} - ${config.service_period || ""} - ${config.invoice_number || "INV"}.${ext}`.replace(/ {2,}/g, " ");
}

// Invoices are generated in the Hub for international engagements (and any
// engagement whose billing says so); New Zealand ones are raised in Xero.
export const invoicesInHub = fields => invoicingOf(parse(fields["Finance Config"]) || {}) === "hub";
export const isUsdEngagement = invoicesInHub; // older name, kept for callers
